import { Fragment } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useViewportType } from '../../utils/useViewportType';
import { config } from '../../../config';
import { PlayButton } from '../PlayButton/PlayButton';
import { FavouritesButton } from '../FavouritesButton/FavouritesButton';
import { EpisodeButton } from '../EpisodeButton/EpisodeButton';
import { SeeMoreButton } from '../SeeMoreButton/SeeMore';
import { useVendor } from '../../VendorPovider/VendorPovider';
import { useAppSelector } from '../../../modules/hooks';
import { selectFavouritesIds } from '../../../modules/favourites/selectors';
import { unleash } from '../../../services/unleash';
import { useIdentity } from '../../IdentityProvider/IdentityProvider';
import { getPath, routeNames } from '../../../shared/helpers/routes';
import { useEpisodeData } from '../EpisodeButton/useEpisodeData';
import { createFavouriteId } from '../../../helpers/favourites';
import { Spacer } from '../../Spacer/Spacer';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { ReactNode, FC } from 'react';

import * as styles from './SplashButtons.module.scss';

interface CategoryButtonsProps {
  category: SvpCategoryAdditional;
  trackSplashClick?: (buttonName: string) => void;
}

const trimSearchParams = (url: string) => url.replace(/\?.+/, '');

const CategoryButtons: FC<CategoryButtonsProps> = ({
  category,
  trackSplashClick,
}) => {
  const leftButtons: ReactNode[] = [];
  const rightButtons: ReactNode[] = [];

  const { isMobile } = useViewportType();
  const { vendor } = useVendor();
  const location = useLocation();
  const { isLoggedIn } = useIdentity();

  const { episodeUrl } = useEpisodeData({
    category,
    vendor,
  });

  const isBrandstudio = vendor === 'brandstudio';

  const shouldDisplayFavourites =
    config.enableFavourites && !isBrandstudio && category.isSeries;

  const categoryUrl = getPath(routeNames.category, {
    vendor,
    category,
  });

  const showSeeMore = !matchPath(
    location.pathname,
    trimSearchParams(categoryUrl)
  );

  const favouritesIds = useAppSelector(selectFavouritesIds);

  const handleButtonClicked = (buttonType: string) => {
    if (trackSplashClick) {
      trackSplashClick(buttonType);
    }
  };

  if (isMobile) {
    if (category?.isSeries && episodeUrl) {
      leftButtons.push(
        <PlayButton
          assetUrl={episodeUrl}
          skipLabel
          onClick={() => handleButtonClicked('PlayButton')}
        />
      );
    }
    if (shouldDisplayFavourites) {
      leftButtons.push(
        <FavouritesButton
          item={category}
          skipLabel={isMobile}
          checked={favouritesIds.includes(createFavouriteId(category))}
          onClick={() => handleButtonClicked('FavouritesButton')}
          isLoggedIn={isLoggedIn}
        ></FavouritesButton>
      );
    }
  } else {
    if (category?.isSeries && unleash.isEnabled('watchtime')) {
      leftButtons.push(
        <EpisodeButton
          category={category}
          vendor={vendor}
          onEpisodeButtonClick={() => handleButtonClicked('EpisodeButton')}
        />
      );
    } else if (episodeUrl) {
      leftButtons.push(
        <PlayButton
          assetUrl={episodeUrl}
          skipLabel
          onClick={() => handleButtonClicked('PlayButton')}
        />
      );
    }

    if (showSeeMore) {
      leftButtons.push(
        <SeeMoreButton
          url={categoryUrl}
          onClick={() => handleButtonClicked('SeeMoreButton')}
        />
      );
    }

    if (shouldDisplayFavourites) {
      rightButtons.push(
        <FavouritesButton
          item={category}
          skipLabel={isMobile}
          checked={favouritesIds.includes(createFavouriteId(category))}
          onClick={() => handleButtonClicked('FavouritesButton')}
          isLoggedIn={isLoggedIn}
        ></FavouritesButton>
      );
    }
  }

  return (
    <>
      <div className={styles.buttonGroup}>
        {leftButtons.map((button, i) => (
          <Fragment key={i}>{button}</Fragment>
        ))}
      </div>
      <Spacer />
      <div className={styles.buttonGroup}>
        {rightButtons.map((button, i) => (
          <Fragment key={i}>{button}</Fragment>
        ))}
      </div>
    </>
  );
};

export { CategoryButtons };
